import { Button, Checkbox, CircularProgress, Grid, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useToast } from "../../../Hooks/useToast";
import { useGetAdminCredentialsMutation } from "../../../Redux/api/adminApi";
import { useRegisterClientMutation } from "../../../Redux/api/clientApi";
import { useRegisterTalentsMutation } from "../../../Redux/api/talentApi";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { KaziAccordion } from "../../../Shared Components/Kazi/KaziAccordion";
import { KaziModal } from "../../../Shared Components/Kazi/KaziModal";
import { ClientType } from "../../../Types/Client";
import { ProfileType } from "../../../Types/General";
import { PrivacyPolicy } from "../../Settings/Components/Policies/PrivacyPolicy";
import { TermsOfService } from "../../Settings/Components/Policies/TOS";
import { setProgress } from "../Slices/accountCreationSlice";
import { useParams, useSearchParams } from "react-router-dom";

interface TermsOfServiceModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>
}
export const TermsOfServiceModal = ({ open, setOpen}:TermsOfServiceModalProps) => {
    /* ******************** Hooks ******************** */
    const { showToast } = useToast();
    const [ searchParams ] = useSearchParams();
    const dispatch = useAppDispatch();
    const [registerTalent, {isLoading: talentRegistrationLoading}] = useRegisterTalentsMutation();
    const [registerClient, {isLoading: clientRegistrationLoading}] = useRegisterClientMutation();
    const [getCredentials, {isLoading: credentialsLoading}] = useGetAdminCredentialsMutation();
    /* ******************** Variables ******************** */
    const accountCreationSlice = useAppSelector( state => state.AccountCreation);
    const [agreeChecked, setAgreeChecked] = useState(false);
    /* ******************** Functions ******************** */
    const handleClose = () => {
        setOpen(false)
    }
    const handleChecked = () => setAgreeChecked(!agreeChecked);
    const handleError = (err: any) => {
        console.log(`Error occured while registering ${ProfileType[accountCreationSlice.ProfileType!]}`);
        console.log(err);
        let toastText = 'Something went wrong'
        switch (err.status){
            case 400:
                const message = err.data.message;
                if (message)
                    toastText = message;
                break;
        }
        showToast({
            text: toastText,
            variant: 'error'
        })
    }
    const submitRegistration = () => {
        if (accountCreationSlice.ProfileType == ProfileType['Client']){
            registerClient({
                body: {
                    first_name: accountCreationSlice.FirstName,
                    last_name: accountCreationSlice.LastName,
                    email: accountCreationSlice.Email,
                    password: accountCreationSlice.Password,
                    phone: accountCreationSlice.PhoneNumber,
                    type: ClientType[accountCreationSlice.ClientType!],
                    company: accountCreationSlice.CompanyName || "",
                    country: accountCreationSlice.Country,
                    date_of_birth: accountCreationSlice.BirthDate
                }
        })
            .unwrap()
            .then(() => dispatch(setProgress(100)))
            .catch((err) => handleError(err))
        } 
        
        if (accountCreationSlice.ProfileType == ProfileType['Talent']) {
            registerTalent({
                body: {
                    first_name: accountCreationSlice.FirstName!,
                    last_name: accountCreationSlice.LastName!,
                    email: accountCreationSlice.Email!,
                    password: accountCreationSlice.Password!,
                    phone: accountCreationSlice.PhoneNumber!,
                    country: accountCreationSlice.Country!,
                    title: " ",
                    industry: " ",
                    date_of_birth: accountCreationSlice.BirthDate!
                },
                affiliate: searchParams.get('affiliate')
            })
            .unwrap()
            .then(() => dispatch(setProgress(100)))
            .catch((err) => handleError(err))
        }
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <KaziModal
            open={open}
            handleClose={handleClose}
            title="Using our services"
            content={
                <>
                    <KaziAccordion 
                        summary="Privacy policy"
                        content={
                            <Grid height={'250px'}>
                                <PrivacyPolicy />
                            </Grid>
                        }
                    />
                    <KaziAccordion 
                        summary="Terms of Service"
                        content={
                            <Grid height={'250px'}>
                                <TermsOfService />
                            </Grid>
                        }
                    />
                    <Grid flex='row'>
                        <Checkbox 
                            checked={agreeChecked}
                            color="primary"
                            onChange={handleChecked}
                        />
                        <Typography variant="body-medium" justifySelf={'center'}>I understand Kazi Konekt’s policies</Typography>
                    </Grid>
                </>
            }
            actions={[
                <Button 
                    variant="outlined"
                    onClick={handleClose}
                >
                    Decline
                </Button>,
                <Button 
                    variant="contained"
                    disabled={!agreeChecked}
                    onClick={submitRegistration}
                    sx={{
                        "&.Mui-disabled": {
                            backgroundColor: 'black.50',
                            border: 'none',
                            color: 'primary.main'
                        }
                    }}
                    endIcon={talentRegistrationLoading || clientRegistrationLoading || credentialsLoading? <CircularProgress color="secondary" size={"18px"}/> :null} 
                >
                    {talentRegistrationLoading || clientRegistrationLoading || credentialsLoading?'Creating account...':'Agree'}
                </Button>
            ]}
        />
    )
}